/**
 * GPU time per frame on the WebGL2 path.
 *
 * The engine measures GPU time itself on WebGPU (engine/src/gpu_timer.rs). On WebGL2 wgpu
 * offers no timestamps, but the browser may offer `EXT_disjoint_timer_query_webgl2`. A
 * second `getContext('webgl2')` on the canvas returns the context wgpu already created, so
 * the viewer can wrap `engine.render()` in a TIME_ELAPSED query. wgpu issues all of a
 * frame's GL calls inside `render()`, so that measures the frame.
 *
 * Firefox hides this extension by default (it can be used to fingerprint), and some GPUs
 * lack it, so it is strictly best-effort: no extension means no GPU time in the HUD.
 *
 * Results arrive a few frames late. Queries are polled, never waited on, so timing never
 * stalls the frame. A "disjoint" flag (GPU reset, power-state change) voids the samples it
 * covers.
 */

const TIME_ELAPSED_EXT = 0x88bf;
const GPU_DISJOINT_EXT = 0x8fbb;
/** Sample every this many frames, like the engine's timer (4 per second at 60 fps). */
const SAMPLE_EVERY = 15;
const MAX_IN_FLIGHT = 4;
const SMOOTHING = 0.3;

/** The slice of WebGL2RenderingContext this needs, so tests can pass a fake. */
export interface TimerGl {
  createQuery(): WebGLQuery | null;
  beginQuery(target: number, query: WebGLQuery): void;
  endQuery(target: number): void;
  getQueryParameter(query: WebGLQuery, pname: number): unknown;
  getParameter(pname: number): unknown;
  getExtension(name: string): unknown;
  readonly QUERY_RESULT: number;
  readonly QUERY_RESULT_AVAILABLE: number;
}

export class GlTimer {
  private frame = 0;
  private active: WebGLQuery | null = null;
  private readonly inFlight: WebGLQuery[] = [];
  private readonly spare: WebGLQuery[] = [];
  private smoothed: number | null = null;

  private constructor(private readonly gl: TimerGl) {}

  /** A timer for `gl`, or null when the browser does not offer GPU timer queries. */
  static create(gl: TimerGl | null): GlTimer | null {
    if (!gl || !gl.getExtension('EXT_disjoint_timer_query_webgl2')) return null;
    return new GlTimer(gl);
  }

  /** Smoothed GPU time in milliseconds, once a sample has arrived. */
  get lastMs(): number | null {
    return this.smoothed;
  }

  /** Call right before `engine.render()`. */
  begin(): void {
    this.poll();
    this.frame++;
    if (this.frame % SAMPLE_EVERY !== 0 || this.inFlight.length >= MAX_IN_FLIGHT) return;
    const query = this.spare.pop() ?? this.gl.createQuery();
    if (!query) return;
    this.gl.beginQuery(TIME_ELAPSED_EXT, query);
    this.active = query;
  }

  /** Call right after `engine.render()`. */
  end(): void {
    if (!this.active) return;
    this.gl.endQuery(TIME_ELAPSED_EXT);
    this.inFlight.push(this.active);
    this.active = null;
  }

  private poll(): void {
    // Read once per poll: it reports whether a disjoint happened since the last read.
    const disjoint = this.inFlight.length > 0 && Boolean(this.gl.getParameter(GPU_DISJOINT_EXT));
    for (;;) {
      const query = this.inFlight[0];
      if (!query || !this.gl.getQueryParameter(query, this.gl.QUERY_RESULT_AVAILABLE)) break;
      this.inFlight.shift();
      const ns = Number(this.gl.getQueryParameter(query, this.gl.QUERY_RESULT));
      this.spare.push(query);
      if (disjoint || !Number.isFinite(ns) || ns <= 0) continue;
      const ms = ns / 1e6;
      this.smoothed = this.smoothed === null ? ms : this.smoothed + SMOOTHING * (ms - this.smoothed);
    }
  }
}
